import { useMemo, useEffect, useState } from "react"
import {
  type BinaryOperator,
  type Filter,
  type Query,
} from "@cubejs-client/core"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import { polyfillCubeQueryWithTimeDimension } from "~/ui-rtk/utils/cube"
import useDepVar from "~/ui-rtk/hooks/dep-var"
import { useAppDispatch, useAppSelector } from "~/ui-rtk/app/hooks"
import { useResolveAssetsMutation } from "~/ui-rtk/api/assetsApi"
import { cacheAssetInfo } from "~/ui-rtk/app/slices/asset-cache.slice"
import { mapMediaChannelToSourceType } from "~/ui-rtk/components/atoms/MarathonImage/helpers"
import {
  buildResolveAssetsBatches,
  mergeResolveAssetsResponses,
} from "~/ui-rtk/utils/resolve-assets-batch"

const ADS_PREFIX = "company_media_metrics_ads_creative_report"
const ORGANIC_PREFIX = "company_media_metrics_organic_posts"

export interface CreativePickerItem {
  id: string
  type: "ad" | "organic"
  title: string
  mediaChannel: string
  thumbnail: string
  brandValue: number
  spend?: number
  purchaseValue?: number
  purchases?: number
  comments?: number
  likes?: number
  shares?: number
  saves?: number
  reach?: number
  resolvedImageUrl?: string
  resolvedThumbnailUrl?: string
}

export interface CreativePickerQueryOptions {
  mode: "ads" | "organic"
  dateRange: [Date, Date]
  sortBy: string
  sortDir: "asc" | "desc"
  offset: number
  limit: number
  columnFilters?: Array<{
    id: string
    value: string | string[]
  }>
}

export default function useCreativePickerQuery({
  mode,
  dateRange,
  sortBy,
  sortDir,
  offset,
  limit,
  columnFilters,
}: CreativePickerQueryOptions) {
  const dispatch = useAppDispatch()
  const cachedAssets = useAppSelector(state => state.assetCacheSlice.assets)
  const cachedUrls = useAppSelector(state => state.assetCacheSlice.urls)
  const [resolveAssetsMutation] = useResolveAssetsMutation()
  const [isResolving, setIsResolving] = useState(false)
  const { depVar, depVarScaler } = useDepVar()

  const cubeQuery = useMemo(() => {
    const prefix = mode === "ads" ? ADS_PREFIX : ORGANIC_PREFIX

    const columnCubeFilters: Filter[] = (columnFilters ?? [])
      .filter(f => {
        if (Array.isArray(f.value)) return f.value.length > 0
        return Boolean(f.value)
      })
      .map(f => {
        const member = f.id.startsWith(prefix) ? f.id : `${prefix}.${f.id}`
        if (Array.isArray(f.value)) {
          return {
            member,
            operator: "equals" as BinaryOperator,
            values: f.value,
          }
        }
        return {
          member,
          operator: "contains" as BinaryOperator,
          values: [f.value],
        }
      })

    if (mode === "ads") {
      const depVarFilters: Filter[] = [
        {
          dimension: `${prefix}.dep_var`,
          operator: "equals" as BinaryOperator,
          values: [depVar],
        },
      ]
      if (depVarScaler.length > 0) {
        depVarFilters.push({
          dimension: `${prefix}.dep_var_scaler`,
          operator: "equals" as BinaryOperator,
          values: depVarScaler,
        })
      }

      const q: Query = {
        dimensions: [
          `${prefix}.creative_id`,
          `${prefix}.root_ad_name`,
          `${prefix}.media_channel`,
          `${prefix}.image_url`,
        ],
        measures: [
          `${prefix}.brand_value_agg`,
          `${prefix}.spend`,
          `${prefix}.purchase_value`,
          `${prefix}.purchases`,
          `${prefix}.comments`,
          `${prefix}.likes`,
          `${prefix}.shares`,
          `${prefix}.saves`,
          `${prefix}.reach`,
          `${prefix}.md_image_url`,
          `${prefix}.thumbnail_url`,
        ],
        filters: [
          {
            dimension: `${prefix}.brand_value_agg`,
            operator: "gt",
            values: ["0"],
          },
          ...depVarFilters,
          ...columnCubeFilters,
        ] as Filter[],
        order: [[`${prefix}.${sortBy}`, sortDir]],
        offset,
        limit,
        total: true,
        timeDimensions: [
          {
            dimension: `${prefix}.md_date`,
          },
        ],
      }

      return polyfillCubeQueryWithTimeDimension(q, { dateRange })
    }

    // Organic mode
    const q: Query = {
      dimensions: [
        `${prefix}.post_id`,
        `${prefix}.post_caption`,
        `${prefix}.media_channel`,
        `${prefix}.md_image_url`,
        `${prefix}.post_preview`,
      ],
      measures: [
        `${prefix}.brand_value_agg`,
        `${prefix}.comments`,
        `${prefix}.likes`,
        `${prefix}.shares`,
        `${prefix}.saves`,
        `${prefix}.reach`,
      ],
      filters: [
        {
          dimension: `${prefix}.brand_value_agg`,
          operator: "gt",
          values: ["0"],
        },
        ...columnCubeFilters,
      ] as Filter[],
      order: [[`${prefix}.${sortBy}`, sortDir]],
      offset,
      limit,
      total: true,
      timeDimensions: [
        {
          dimension: `${prefix}.md_date`,
        },
      ],
    }

    return polyfillCubeQueryWithTimeDimension(q, { dateRange })
  }, [
    mode,
    dateRange,
    sortBy,
    sortDir,
    offset,
    limit,
    columnFilters,
    depVar,
    depVarScaler,
  ])

  const { resultSet, isLoading } = useCubeQuery(cubeQuery)

  const totalCount = useMemo(() => {
    if (!resultSet) return 0
    return resultSet.totalRows() ?? 0
  }, [resultSet])

  const data = useMemo<CreativePickerItem[]>(() => {
    if (!resultSet) return []
    const prefix = mode === "ads" ? ADS_PREFIX : ORGANIC_PREFIX
    const rows = resultSet.rawData() as Record<string, string>[]

    if (mode === "ads") {
      return rows
        .filter(row => row[`${prefix}.creative_id`])
        .map(row => ({
          id: row[`${prefix}.creative_id`] || "",
          type: "ad" as const,
          title: row[`${prefix}.root_ad_name`] || "",
          mediaChannel: row[`${prefix}.media_channel`] || "",
          thumbnail:
            row[`${prefix}.md_image_url`] ||
            row[`${prefix}.thumbnail_url`] ||
            row[`${prefix}.image_url`] ||
            "",
          brandValue: Number(row[`${prefix}.brand_value_agg`]) || 0,
          spend: Number(row[`${prefix}.spend`]) || 0,
          purchaseValue: Number(row[`${prefix}.purchase_value`]) || 0,
          purchases: Number(row[`${prefix}.purchases`]) || 0,
          comments: Number(row[`${prefix}.comments`]) || 0,
          likes: Number(row[`${prefix}.likes`]) || 0,
          shares: Number(row[`${prefix}.shares`]) || 0,
          saves: Number(row[`${prefix}.saves`]) || 0,
          reach: Number(row[`${prefix}.reach`]) || 0,
        }))
    }

    return rows
      .filter(row => row[`${prefix}.post_id`])
      .map(row => ({
        id: row[`${prefix}.post_id`] || "",
        type: "organic" as const,
        title: row[`${prefix}.post_caption`] || "",
        mediaChannel: row[`${prefix}.media_channel`] || "",
        thumbnail:
          row[`${prefix}.md_image_url`] || row[`${prefix}.post_preview`] || "",
        brandValue: Number(row[`${prefix}.brand_value_agg`]) || 0,
        comments: Number(row[`${prefix}.comments`]) || 0,
        likes: Number(row[`${prefix}.likes`]) || 0,
        shares: Number(row[`${prefix}.shares`]) || 0,
        saves: Number(row[`${prefix}.saves`]) || 0,
        reach: Number(row[`${prefix}.reach`]) || 0,
      }))
  }, [resultSet, mode])

  // Resolve asset URLs
  useEffect(() => {
    if (data.length === 0) return

    const isOrganic = mode === "organic"
    const uncached: Array<{
      sourceType: ReturnType<typeof mapMediaChannelToSourceType>
      externalRefId: string
      url?: string
    }> = []

    for (const row of data) {
      const sourceType = mapMediaChannelToSourceType(
        row.mediaChannel,
        isOrganic,
      )
      const key = `${sourceType}:${row.id}`
      if (cachedUrls[key]) continue

      uncached.push({
        sourceType,
        externalRefId: row.id,
        ...(row.thumbnail && { url: row.thumbnail }),
      })
    }

    const batches = buildResolveAssetsBatches(uncached)
    if (batches.length === 0) return

    setIsResolving(true)
    void Promise.all(
      batches.map(batch => resolveAssetsMutation({ assets: batch }).unwrap()),
    )
      .then(responses => mergeResolveAssetsResponses(responses))
      .then(result => {
        for (const [key, asset] of Object.entries(result.results)) {
          if (!asset) continue

          let resolvedUrl =
            asset.syncStatus === "SYNCED"
              ? asset.url || asset.thumbnailUrl || null
              : null

          if (!resolvedUrl) {
            const match = uncached.find(
              a => `${a.sourceType}:${a.externalRefId}` === key,
            )
            resolvedUrl = match?.url || null
          }

          if (resolvedUrl) {
            dispatch(
              cacheAssetInfo({
                key,
                info: {
                  url: resolvedUrl,
                  thumbnailUrl: asset.thumbnailUrl || resolvedUrl,
                  mediaType: asset.mediaType,
                  width: asset.width,
                  height: asset.height,
                  assetId: asset.id,
                },
              }),
            )
          }
        }
      })
      .finally(() => setIsResolving(false))
  }, [data, cachedUrls, resolveAssetsMutation, dispatch, mode])

  // Enrich with resolved URLs
  const items = useMemo<CreativePickerItem[]>(
    () =>
      data.map(row => {
        const isOrganic = row.type === "organic"
        const sourceType = mapMediaChannelToSourceType(
          row.mediaChannel,
          isOrganic,
        )
        const key = `${sourceType}:${row.id}`
        const cached = cachedAssets[key]
        return {
          ...row,
          resolvedImageUrl: cached?.url,
          resolvedThumbnailUrl: cached?.thumbnailUrl,
        }
      }),
    [data, cachedAssets],
  )

  return {
    items,
    totalCount,
    isLoading,
    isResolving,
  }
}
